// Location data processing utilities
import allCountries from "./AllCountries"
import geoInfo from "./geoInfo"
import * as countries from 'countries-list'

interface LocationDataItem {
  country: string
  region: string
  province: string
}

// Lazy load location data to avoid initialization issues
let locationData: LocationDataItem[] | null = null

const getLocationData = (): LocationDataItem[] => {
  if (!locationData) {
    try {
      locationData = [
        ...geoInfo.map(item => ({
          country: "Afghanistan",
          region: item.region_name,
          province: item.province
        })),
        ...allCountries.map(item => ({
          country: item.Country,
          region: item.Region,
          province: item.Province
        }))
      ]
    } catch (error) {
      console.error('Error loading location data:', error)
      locationData = []
    }
  }
  return locationData
}


export const getUniqueCountries = (): Array<{ id: string; name: string }> => {
  // Use the same countries-list package as user management for consistency
  const countriesList = Object.values(countries.countries).map((country: { name: string }) => country.name)
  return countriesList.map((country) => ({
    id: country,
    name: country,
  }))
}

export const getUniqueRegions = (countryName?: string, provinceName?: string): Array<{ id: string; name: string }> => {
  const data = getLocationData()
  let filteredData = data

  if (countryName) {
    filteredData = data.filter((item) => item.country === countryName)
  }

  if (provinceName) {
    filteredData = filteredData.filter((item) => item.province === provinceName)
  }

  const uniqueRegions = [...new Set(filteredData.map((item) => item.region))]
  return uniqueRegions.map((region) => ({
    id: region,
    name: region,
  }))
}

export const getUniqueProvinces = (countryName?: string, regionName?: string): Array<{ id: string; name: string }> => {
  const data = getLocationData()
  let filteredData = data

  if (countryName) {
    filteredData = filteredData.filter((item) => item.country === countryName)
  }

  if (regionName) {
    filteredData = filteredData.filter((item) => item.region === regionName)
  }

  const uniqueProvinces = [...new Set(filteredData.map((item) => item.province))]
  return uniqueProvinces.map((province) => ({
    id: province,
    name: province,
  }))
}

export const getUniqueDistricts = (countryName?: string, provinceName?: string): Array<{ id: string; name: string }> => {
  try {
    let filteredData = geoInfo

    if (countryName) {
      // Filter by country if provided (assuming Afghanistan for geoInfo)
      if (countryName !== "Afghanistan") {
        return []
      }
    }

    if (provinceName) {
      filteredData = filteredData.filter((item: any) => item.province === provinceName)
    }

    const uniqueDistricts = [...new Set(filteredData.map((item: any) => item.district).filter(Boolean))]
    return uniqueDistricts.map((district) => ({
      id: district,
      name: district,
    }))
  } catch (error) {
    console.error('Error getting district data:', error)
    return []
  }
}

// Get all unique values for initial load
export const getAllLocationData = () => {
  try {
    return {
      countries: getUniqueCountries(),
      regions: getUniqueRegions(),
      provinces: getUniqueProvinces(),
      districts: [] as Array<{ id: string; name: string }>
    }
  } catch (error) {
    console.error('Error getting location data:', error)
    return {
      countries: [],
      regions: [],
      provinces: [],
      districts: []
    }
  }
}
